import React, { FC, useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  IconButton,
  InputAdornment,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import axios from 'axios'
import Tree from '../tree/Tree'
import useAccount from '../../hooks/useAccount'
import useVariableTree from '../../hooks/useVariableTree'
import {
  andFilters,
  createFilterFromSelectedVariables,
} from '../../utils/filter'
import { API_BASE_URL } from '../../constants/system'
import { extractErrorMessage } from '../../utils/apitools'

export interface FilterEditorModalProps {
  open: boolean
  onClose: () => void
  onSave: (filterString: string) => void
  currentFilter?: string
  customer?: string
  job?: string
  /**
   * Multi-job quick filter (#1229 C7): when more than one job is offered the
   * dialog shows a job selector. Each job holds its own expression
   * (`filtersByJob`); switching job swaps the expression field to that job's
   * current filter, or blank when it has none. Save then reports the job it
   * was written for via `onSaveForJob`. The selected job drives the variable
   * tree and validation.
   */
  jobs?: { name: string; displayName?: string }[]
  filtersByJob?: Record<string, string>
  onSaveForJob?: (job: string, filterString: string) => void
  /**
   * Hide the Survey Data / AutoCoder Data source selector (#1229 A6, Matt 3
   * Sep): dashboards always filter on Survey Data until Theme AI is signed
   * off. Tables keep the selector.
   */
  hideSourceSelector?: boolean
  /**
   * Checks an expression for a job and returns the refusal to show, or null.
   * Without it the dialog asks Carbon (POST .../validate/expression). The
   * respondent data view (#1887) passes its own: goapi evaluates that filter
   * against the case data, which reads a smaller grammar than Carbon.
   */
  validate?: (job: string, expr: string) => Promise<string | null>
}

const FilterEditorModal: FC<FilterEditorModalProps> = ({
  open,
  onClose,
  onSave,
  currentFilter = '',
  customer: customerOverride,
  job: jobOverride,
  jobs,
  filtersByJob,
  onSaveForJob,
  hideSourceSelector = false,
  validate,
}) => {
  const account = useAccount()
  const effectiveCustomer = customerOverride || account.customer?.name || ''
  const hasJobSelector = Boolean(jobs && jobs.length > 1)
  const [selectedJob, setSelectedJob] = useState(jobOverride || jobs?.[0]?.name || '')
  const effectiveJob = (hasJobSelector ? selectedJob : jobOverride) || account.job?.name || ''
  // #1240 A3: with a job selector the dialog keeps a DRAFT per job for as
  // long as it is open — switching jobs never loses what was typed, and Save
  // applies every job whose draft changed. A job whose draft is blank and
  // was blank to start with is left alone (clearing stays on the chip's X).
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  const [editableFilter, setEditableFilter] = useState(currentFilter || '')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [isValidating, setIsValidating] = useState(false)
  const [driver, setDriver] = useState<'carbon' | 'autocoder'>('carbon')

  const isManager = account.user?.role_permissions?.isManager || false

  const variableTree = useVariableTree({
    customer: effectiveCustomer,
    job: effectiveJob,
    driver,
  })

  const validateAgainst = async (job: string, expr: string): Promise<string | null> => {
    if (!expr.trim() || !effectiveCustomer || !job) return null
    if (validate) return validate(job, expr)
    try {
      await axios.post(`${API_BASE_URL}/tables/${effectiveCustomer}/${job}/validate/expression`, { syntax: expr })
      return null
    } catch (error) {
      return extractErrorMessage(error)
    }
  }

  const validateFilter = async (filterSyntax: string) => {
    if (!filterSyntax || !effectiveCustomer || !effectiveJob) {
      setValidationError(null)
      return
    }
    setIsValidating(true)
    try {
      setValidationError(await validateAgainst(effectiveJob, filterSyntax))
    } finally {
      setIsValidating(false)
    }
  }

  useEffect(() => {
    if (open) {
      const initialJob = jobOverride || jobs?.[0]?.name || ''
      setSelectedJob(initialJob)
      setDrafts({ ...(filtersByJob || {}) })
      setEditableFilter((hasJobSelector ? filtersByJob?.[initialJob] : currentFilter) || '')
      setValidationError(null)
      variableTree.setChecked([])
    }
  }, [open, currentFilter])

  const handleJobChange = (job: string) => {
    setDrafts((prev) => ({ ...prev, [selectedJob]: editableFilter }))
    setSelectedJob(job)
    setEditableFilter(drafts[job] ?? filtersByJob?.[job] ?? '')
    setValidationError(null)
    variableTree.setChecked([])
  }

  // Every job whose draft differs from what it opened with, the current
  // job's field included (it is the live draft).
  const changedDrafts = (): [string, string][] => {
    const all = { ...drafts, [selectedJob]: editableFilter }
    return Object.entries(all).filter(([job, expr]) => (expr || '').trim() !== (filtersByJob?.[job] || '').trim())
  }
  const hasChanges = hasJobSelector ? changedDrafts().length > 0 : Boolean(editableFilter.trim())

  const handleApplyTreeSelection = useCallback(() => {
    const selection = createFilterFromSelectedVariables(variableTree.selectedVariables, variableTree.nodeMap)
    if (selection) {
      const newFilter = andFilters(editableFilter, selection)
      setEditableFilter(newFilter)
      validateFilter(newFilter)
      variableTree.resetChecked()
    }
  }, [variableTree.selectedVariables, variableTree.nodeMap, editableFilter, validateFilter])

  const handleSave = useCallback(async () => {
    setIsValidating(true)
    try {
      if (hasJobSelector && onSaveForJob) {
        // Validate each changed draft against ITS job; on a failure, show
        // that job so the error is next to the expression it belongs to.
        const changes = changedDrafts()
        for (const [job, expr] of changes) {
          const err = await validateAgainst(job, expr)
          if (err) {
            if (job !== selectedJob) {
              setDrafts((prev) => ({ ...prev, [selectedJob]: editableFilter }))
              setSelectedJob(job)
              setEditableFilter(expr)
            }
            setValidationError(err)
            return
          }
        }
        setValidationError(null)
        for (const [job, expr] of changes) onSaveForJob(job, expr)
      } else {
        const err = await validateAgainst(effectiveJob, editableFilter)
        if (err) { setValidationError(err); return }
        setValidationError(null)
        onSave(editableFilter)
      }
    } finally {
      setIsValidating(false)
    }
    onClose()
  }, [editableFilter, drafts, selectedJob, onSave, onSaveForJob, hasJobSelector, onClose, effectiveCustomer, effectiveJob, filtersByJob, validate])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', display: 'flex', flexDirection: 'column', borderRadius: 0 }
      }}
    >
      <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
        {/* Title + close button */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontSize: '0.75rem',
            }}>
              Filter Editor
            </Typography>
            <Tooltip title="Use this editor to build table filters. Select variables and codes from the variable tree below, then click the green add button to add them to your filter expression.">
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close filter editor" sx={{ color: 'text.secondary' }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Filter bar */}
        <Box sx={{ px: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Apply selected variables to filter">
              <span>
                <IconButton
                  size="small"
                  aria-label="Apply selected variables to filter"
                  onClick={handleApplyTreeSelection}
                  disabled={variableTree.selectedVariables.length === 0}
                  sx={variableTree.selectedVariables.length > 0 ? {
                    backgroundColor: 'success.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'success.dark' },
                  } : {
                    color: 'text.secondary',
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <TextField
              value={editableFilter}
              onChange={(e) => {
                setEditableFilter(e.target.value)
                if (validationError) setValidationError(null)
              }}
              onBlur={() => {
                if (editableFilter && editableFilter.trim()) {
                  validateFilter(editableFilter)
                } else {
                  setValidationError(null)
                }
              }}
              size="small"
              fullWidth
              placeholder="Filter expression..."
            />
            <Button
              variant="text"
              size="small"
              onClick={onClose}
              sx={{ flexShrink: 0, color: 'text.secondary', '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' } }}
            >
              Cancel
            </Button>
            {hasChanges && (
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={!!validationError || isValidating}
                sx={{ flexShrink: 0 }}
              >
                Save
              </Button>
            )}
          </Box>

          {validationError && (
            <Alert severity="error" sx={{ mt: 1, borderRadius: 0 }}>
              {validationError}
            </Alert>
          )}
        </Box>

        {/* Variable Tree section */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', px: 2, pt: 2 }}>
          <Typography variant="subtitle2" sx={{
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: '0.75rem',
            mb: 1,
          }}>
            Variable Tree
          </Typography>

          {hasJobSelector && (
            <FormControl size="small" fullWidth sx={{ mb: 1 }}>
              <Select
                value={selectedJob}
                onChange={(e) => handleJobChange(e.target.value as string)}
                inputProps={{ 'aria-label': 'Filter job' }}
              >
                {jobs!.map((j) => (
                  <MenuItem key={j.name} value={j.name}>{j.displayName || j.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {isManager && !hideSourceSelector && (
            <FormControl size="small" fullWidth sx={{ mb: 1 }}>
              <Select
                value={driver}
                onChange={(e) => setDriver(e.target.value as 'carbon' | 'autocoder')}
                inputProps={{ 'aria-label': 'Data source' }}
              >
                <MenuItem value="carbon">Survey Data</MenuItem>
                <MenuItem value="autocoder">AutoCoder Data</MenuItem>
              </Select>
            </FormControl>
          )}

          {variableTree.nodes && variableTree.nodes.length > 0 ? (
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Tree
                searchEnabled={true}
                showTooltips
                searchPlaceholder="Search variables..."
                nodes={variableTree.nodes}
                expanded={variableTree.expanded}
                getCheckboxState={variableTree.getCheckboxState}
                onNodeExpandToggle={variableTree.onNodeExpandToggle}
                onNodeCheckedToggle={variableTree.onNodeCheckedToggle}
              />
            </Box>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">No variables available</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default FilterEditorModal
